/**
 * A rave opens only for somebody ATTENDING it (the user, 2026-10-15: "a user
 * can't access any rave without attending it first — buy or free"). The API's
 * bundle answers 403 `attend_first`; this is what the rave shows instead of
 * its lineup: the rave itself — cover, logo, name, dates, place — and Attend.
 * A paid rave never gets here: its 402 is the paywall, and buying it attends.
 *
 * Attending is `POST /editions/:id/attend` (`attendEdition`, which also
 * counts the attend), then the lineup is asked for again.
 */
import { useEffect, useRef, useState } from 'react';
import { hasPendingSave } from '@/lib/signInFirst';
import { ApiError } from '@/lib/api';
import { Button, Stack, Txt, localDateRange, localCountry } from '@/ui';
import { Alert } from './authControls';
import { useT } from '@/i18n';
import { useTenant } from '@/app/TenantProvider';
import { formatDateRange } from '@myrave/shared';
import { RaveStrip } from './RaveOptions';
import { attendEdition } from './Directory';
import './AttendFirst.css';
import { useLocale } from '@/app/LocaleProvider';
import { useInviteFrom } from '@/app/useInvite';

export function AttendFirst({ editionId, festivalName, onAttended }: { editionId: string; festivalName: string; onAttended: () => void }) {
  const tr = useT();
  const { locale } = useLocale();
  const { tenant } = useTenant();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const edition = tenant?.editions?.find((e) => e.id === editionId);
  // Arrived by a friend's invite (2026-10-27): "Dana invited you to join OZORA 2026".
  const invite = useInviteFrom([editionId]);
  const dates = edition?.gatesOpenAt && edition.endsAt
    ? localDateRange(locale, formatDateRange(new Date(edition.gatesOpenAt), new Date(edition.endsAt), edition.timezone), new Date(edition.gatesOpenAt), new Date(edition.endsAt), edition.timezone)
    : null;
  // The country by NAME in the reader's language, not its code ("HU").
  const place = [edition?.venue ?? tenant?.venue, localCountry(locale, tenant?.country)].filter(Boolean).join(' · ');
  const attend = async () => {
    setBusy(true);
    setError(null);
    try {
      await attendEdition(editionId);
      onAttended();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : tr('Could not reach the server. Check your connection and try again.'));
    } finally {
      setBusy(false);
    }
  };
  /*
   * Back from the sign-in a SAVE asked for (2026-10-26): saving a set of this rave is saying
   * "I'm going" — it attends by itself, once, and the set is saved when the lineup opens.
   */
  const auto = useRef(false);
  useEffect(() => {
    if (auto.current || !hasPendingSave()) return;
    auto.current = true;
    void attend();
  }, [editionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="attend-first">
      <Stack gap={14}>
        {invite && (
          <p className="attend-first-invited">
            {tr('{name} invited you to join {rave}', { name: invite.inviterName, rave: invite.editionName })}
          </p>
        )}
        <RaveStrip name={festivalName} detail={[dates, place].filter(Boolean).join(' · ') || null}
          logoUrl={edition?.logoUrl ?? tenant?.logoUrl} coverUrl={tenant?.coverUrl}>
          <Txt as="p" size={13} lh={1.5}>
            {tr('Attend {rave} to open its lineup, map and everything offline.', { rave: festivalName })}
          </Txt>
        </RaveStrip>
        {error && <Alert>{error}</Alert>}
        <Button onClick={() => void attend()} loading={busy} loadingLabel={tr('Joining…')}>
          {tr('Attend {rave}', { rave: festivalName })}
        </Button>
      </Stack>
    </div>
  );
}
